/**
 * `h()` — the tiny DOM helper of the UI kit (architecture §3.10; no UI framework). Owner: UI/Visual (F3).
 * F0 ships a working version so the stub screens compile and render; F3 may extend it but keeps the signature.
 *
 * Signals passed as children, `class`, `style` values, `attrs` values or `text` stay live: each binding subscribes and
 * registers its unsubscribe on the element; call {@link disposeTree} (screens: in `dispose()`) to release them.
 */
import { isSignal, type ReadonlySignal } from '../shared';

export type Child = Node | string | number | ReadonlySignal<string | number> | null | false | undefined | Child[];
export interface Props {
  class?: string | ReadonlySignal<string>;
  style?: string | Partial<Record<string, string | ReadonlySignal<string>>>;
  /** Incl. data-*, aria-*. `false` removes the attribute, `true` sets it empty. */
  attrs?: Record<string, string | number | boolean | ReadonlySignal<string | number | boolean>>;
  on?: Partial<{ [K in keyof HTMLElementEventMap]: (e: HTMLElementEventMap[K]) => void }>;
  ref?: (el: HTMLElement) => void;
  text?: ReadonlySignal<string | number>;
}

const disposers = new WeakMap<Node, Array<() => void>>();

function own(node: Node, dispose: () => void): void {
  const list = disposers.get(node);
  if (list) list.push(dispose); else disposers.set(node, [dispose]);
}

function bind<T>(owner: Node, v: T | ReadonlySignal<T>, apply: (x: T) => void): void {
  if (isSignal<T>(v)) {
    apply(v.get());
    own(owner, v.subscribe(apply));
  } else {
    apply(v as T);
  }
}

function setAttr(el: HTMLElement, name: string, v: string | number | boolean): void {
  if (v === false) el.removeAttribute(name);
  else el.setAttribute(name, v === true ? '' : String(v));
}

function append(parent: HTMLElement, child: Child): void {
  if (child === null || child === undefined || child === false) return;
  if (Array.isArray(child)) { for (const c of child) append(parent, c); return; }
  if (typeof child === 'string' || typeof child === 'number') { parent.appendChild(document.createTextNode(String(child))); return; }
  if (isSignal<string | number>(child)) {
    const text = document.createTextNode('');
    bind(text, child, (x) => { text.data = String(x); });
    parent.appendChild(text);
    return;
  }
  parent.appendChild(child as Node);
}

/** Creates an element with props and children. */
export function h<K extends keyof HTMLElementTagNameMap>(tag: K, props?: Props | null, ...children: Child[]): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  if (props) {
    if (props.class !== undefined) bind(el, props.class, (c) => { el.className = c; });
    if (typeof props.style === 'string') el.style.cssText = props.style;
    else if (props.style) {
      for (const [k, v] of Object.entries(props.style)) {
        if (v === undefined) continue;
        bind(el, v, (x) => { if (k.startsWith('--')) el.style.setProperty(k, x); else (el.style as unknown as Record<string, string>)[k] = x; });
      }
    }
    if (props.attrs) for (const [k, v] of Object.entries(props.attrs)) bind(el, v, (x) => setAttr(el, k, x));
    if (props.on) {
      for (const [type, fn] of Object.entries(props.on)) {
        if (fn) el.addEventListener(type, fn as EventListener);
      }
    }
    if (props.text) bind(el, props.text, (x) => { el.textContent = String(x); });
  }
  for (const c of children) append(el, c);
  props?.ref?.(el);
  return el;
}

/** Releases every signal binding made by `h()` inside `root` (inclusive). */
export function disposeTree(root: Node): void {
  const walk = (n: Node): void => {
    const list = disposers.get(n);
    if (list) { disposers.delete(n); for (const d of list) d(); }
    for (let c = n.firstChild; c; c = c.nextSibling) walk(c);
  };
  walk(root);
}
